import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './cron-generator.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

interface CronState {
    fields: [string, string, string, string, string];
    expression: string;
    error: string;
    copied: boolean;
    mounted: boolean;
}

const FIELD_NAMES = ['Minute', 'Hour', 'Day (Month)', 'Month', 'Day (Week)'] as const;
const PRESETS: Record<string, [string, string, string, string, string]> = {
    'Every minute': ['*', '*', '*', '*', '*'],
    'Hourly': ['0', '*', '*', '*', '*'],
    'Daily at midnight': ['0', '0', '*', '*', '*'],
    'Weekly on Monday': ['0', '0', '*', '*', '1'],
    'Monthly on 1st': ['0', '0', '1', '*', '*'],
};

const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function describe(fields: string[]): string {
    const [min, hour, dom, mon, dow] = fields;
    const parts: string[] = [];

    if (min === '*' && hour === '*') parts.push('Every minute');
    else if (min === '*') parts.push(`Every minute of hour ${hour}`);
    else if (hour === '*') parts.push(`At minute ${min} of every hour`);
    else parts.push(`At ${hour.padStart(2, '0')}:${min.padStart(2, '0')}`);

    if (dom !== '*') parts.push(`on day ${dom} of the month`);
    if (mon !== '*') {
        const m = Number(mon);
        parts.push(`in ${(m >= 1 && m <= 12) ? MONTHS[m] : `month ${mon}`}`);
    }
    if (dow !== '*') {
        const d = Number(dow);
        parts.push(`on ${(d >= 0 && d <= 6) ? DAYS[d] : `day ${dow}`}`);
    }
    return parts.join(' ') || 'Every minute';
}

function getNextExecutions(fields: string[], count: number): Date[] {
    const results: Date[] = [];
    const now = new Date();
    const d = new Date(now);
    d.setSeconds(0, 0);
    d.setMinutes(d.getMinutes() + 1);

    const match = (val: string, current: number) => {
        if (val === '*') return true;
        if (val.includes('/')) { const step = Number(val.split('/')[1]); return current % step === 0; }
        if (val.includes(',')) return val.split(',').map(Number).includes(current);
        if (val.includes('-')) { const [a, b] = val.split('-').map(Number); return current >= a && current <= b; }
        return Number(val) === current;
    };

    for (let i = 0; i < 525600 && results.length < count; i++) {
        if (match(fields[0], d.getMinutes()) && match(fields[1], d.getHours()) &&
            match(fields[2], d.getDate()) && match(fields[3], d.getMonth() + 1) &&
            match(fields[4], d.getDay())) {
            results.push(new Date(d));
        }
        d.setMinutes(d.getMinutes() + 1);
    }
    return results;
}

export default class UcronUgeneratorComponent extends Component<{ feature?: string }, CronState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    state: CronState = {
        fields: ['*', '*', '*', '*', '*'],
        expression: '* * * * *',
        error: '', copied: false,
        mounted: false,
    };

    componentDidMount() {
        this.setState({ mounted: true });
    }

    setFields = (fields: [string, string, string, string, string]) => {
        this.setState({ fields, expression: fields.join(' '), error: '' });
    };

    setField = (index: number, value: string) => {
        const fields = [...this.state.fields] as [string, string, string, string, string];
        fields[index] = value;
        this.setFields(fields);
    };

    parseExpression = (expr: string) => {
        this.setState({ expression: expr, error: '' });
        const parts = expr.trim().split(/\s+/);
        if (parts.length !== 5) { this.setState({ error: 'Cron expression must have exactly 5 fields' }); return; }
        this.setState({ fields: parts as [string, string, string, string, string] });
    };

    copy = () => {
        navigator.clipboard.writeText(this.state.expression).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { fields, expression, error, copied, mounted } = this.state;
        const activePreset = Object.entries(PRESETS).find(([, v]) => v.join(' ') === fields.join(' '))?.[0];
        // Next-run timestamps depend on the current clock — defer to after
        // mount so SSR and client renders agree (avoids React #418 mismatch).
        const nextExecs = mounted ? getNextExecutions(fields, 10) : [];

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Presets</label>
                        <div className={styles.presets}>
                            {Object.entries(PRESETS).map(([name, val]) => (
                                <button key={name} className={`${styles.preset} ${activePreset === name ? styles.presetActive : ''}`}
                                    onClick={() => this.setFields(val)}>{name}</button>
                            ))}
                        </div>
                    </div>

                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Visual Builder</label>
                        <div className={styles.builder}>
                            {FIELD_NAMES.map((name, i) => (
                                <div key={name} className={styles.field}>
                                    <span className={styles.fieldLabel}>{name}</span>
                                    <input className={styles.fieldInput} value={fields[i]}
                                        onChange={e => this.setField(i, e.target.value)} />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Cron Expression</label>
                        <div className={styles.expressionRow}>
                            <input className={`${styles.expressionInput} ${error ? sharedStyles.inputError : ''}`}
                                value={expression} onChange={e => this.parseExpression(e.target.value)} />
                            <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copy}>{copied ? '✓ Copied' : 'Copy'}</button>
                        </div>
                        {error && <div className={sharedStyles.errorMessage}>{error}</div>}

                        <div className={styles.description}>{describe(fields)}</div>
                    </div>

                    {nextExecs.length > 0 && (
                        <div className={genStyles.section}>
                            <label className={sharedStyles.label}>Next 10 Runs</label>
                            <div className={styles.executions}>
                                {nextExecs.map((d, i) => (
                                    <div key={i} className={styles.execItem}>{d.toLocaleString()}</div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
